import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { auth } from '../firebase/config';

const DocusignCallbackPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Connecting your DocuSign account...');
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      setError('No authorization code found in the URL.');
      return;
    }

    const getFirebaseToken = () => {
      return new Promise((resolve, reject) => {
        if (auth.currentUser) {
          auth.currentUser.getIdToken(true).then(resolve).catch(reject);
          return;
        }
        const timeoutId = setTimeout(() => {
          unsubscribe();
          reject(new Error('Firebase auth timeout'));
        }, 10000);
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
          clearTimeout(timeoutId);
          unsubscribe();
          if (user) {
            try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
          } else {
            reject(new Error('No authenticated user'));
          }
        });
      });
    };

    const handleCallback = async () => {
      try {
        const firebaseToken = await getFirebaseToken();
        await apiClient.post(
          API_ENDPOINTS.docusignCallback,
          { code },
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        setStatus('DocuSign connected successfully! Redirecting...');

        // If Add Form / Send for Signature stashed a resume intent before sending the
        // user here to connect (see TransactionDocuments.js — full-page OAuth redirects
        // don't preserve in-memory state, so intent is stashed in sessionStorage instead),
        // return to that transaction's Docs tab instead of Connected Services so the
        // blocked action can resume automatically. Left in sessionStorage for
        // TransactionDocuments to read + clear on mount.
        let resumeTransactionId = null;
        try {
          const raw = sessionStorage.getItem('docusign_resume_intent');
          const parsed = raw ? JSON.parse(raw) : null;
          if (parsed?.transactionId && parsed?.ts && Date.now() - parsed.ts <= 10 * 60 * 1000) {
            resumeTransactionId = parsed.transactionId;
          }
        } catch { /* ignore — fall back to Connected Services */ }

        setTimeout(() => {
          if (resumeTransactionId) {
            navigate(`/transaction/${resumeTransactionId}`, { state: { activeTab: 'docs' } });
          } else {
            navigate('/connected-services');
          }
        }, 2000);
      } catch (err) {
        console.error('DocuSign callback error:', err);
        setError('Failed to connect DocuSign. Please try again.');
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'Inter, sans-serif',
      color: '#1E293B',
    }}>
      {error ? (
        <>
          <p style={{ color: '#DC2626', fontSize: '16px', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #3730A3, #4F46E5)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Back to Dashboard
          </button>
        </>
      ) : (
        <>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #E0E7FF',
            borderTopColor: '#4F46E5',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            marginBottom: '20px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ fontSize: '16px', color: '#4B5563' }}>{status}</p>
        </>
      )}
    </div>
  );
};

export default DocusignCallbackPage;
